import React, { useState, useEffect, useMemo } from "react";
import {
  XYChart as VisxXYChart,
  Grid,
  Tooltip,
  Axis,
  BarStack,
} from "@visx/xychart";
import { ParentSize } from "@visx/responsive";
import { PatternLines } from "@visx/pattern";
import { curveLinear, curveMonotoneX } from "@visx/curve";
import type { XYChartConfig, TooltipData } from "../../types";
import { useChartSettings } from "../../hooks/useChartSettings";
import { useChartData } from "../../hooks/useChartData";
import { renderSeries } from "./renderSeries";
import { configureScales } from "./configureScales";
import { getLightTheme } from "../../styles/themes/lightTheme";
import { getDarkTheme } from "../../styles/themes/darkTheme";
import { ChartLegend, type LegendItem } from "../Legend";
import {
  CHART_MARGIN,
  LEGEND_WIDTH,
  LEGEND_HEIGHT,
  LEGEND_PADDING,
  PATTERN_OPACITY,
  DEFAULT_GLYPH_SIZE,
  MIN_PIXELS_PER_TICK,
  DEFAULT_X_TICKS,
  MIN_X_TICKS,
  MAX_X_TICKS,
  TICK_DENSITY_THRESHOLDS,
  WIDTH_BREAKPOINTS,
} from "../../constants";

function resolveChartMargin(
  padding?: Partial<{ top: number; right: number; bottom: number; left: number }>,
) {
  return {
    top: padding?.top ?? CHART_MARGIN.top,
    right: padding?.right ?? CHART_MARGIN.right,
    bottom: padding?.bottom ?? CHART_MARGIN.bottom,
    left: padding?.left ?? CHART_MARGIN.left,
  };
}

/** Reserve enough vertical space for bottom legends (inline wrap or stacked rows). */
export function estimateBottomLegendHeight(
  items: { label: string }[],
  width: number,
  mode: "stacked" | "inline",
): number {
  if (!items.length) return 0;
  const usableWidth = Math.max(width, 120);

  if (mode === "stacked") {
    const charsPerLine = Math.max(16, Math.floor((usableWidth - 28) / 6.5));
    const rowHeight = 20;
    const body = items.reduce((sum, item) => {
      const lines = Math.max(1, Math.ceil(String(item.label).length / charsPerLine));
      return sum + lines * rowHeight + 4;
    }, 0);
    return Math.max(LEGEND_HEIGHT, body + 8);
  }

  const estimatedItemsPerRow = Math.max(1, Math.floor(usableWidth / 120));
  const estimatedLegendRows = Math.ceil(items.length / estimatedItemsPerRow);
  const longestLabel = items.reduce((max, item) => Math.max(max, item.label.length), 0);
  const wrapLinesPerItem = Math.max(1, Math.ceil(longestLabel / Math.max(12, charsPerLineFromWidth(usableWidth))));
  const rowHeight = 22 * wrapLinesPerItem;
  return Math.max(LEGEND_HEIGHT, estimatedLegendRows * rowHeight + 12);
}

function charsPerLineFromWidth(width: number): number {
  return Math.max(12, Math.floor((width - 40) / 6.5));
}

export type BottomLegendHeightMode = "simple" | "wrapEstimate";

/** Bottom legend vertical reserve; `simple` matches pre–chat-embed XYChart behaviour. */
export function computeBottomLegendHeight(
  mode: BottomLegendHeightMode,
  items: { label: string }[],
  width: number,
  legendMode: "stacked" | "inline",
): number {
  if (mode === "wrapEstimate") {
    return estimateBottomLegendHeight(items, width, legendMode);
  }
  const legendItemCount = items.length;
  const estimatedItemsPerRow = Math.max(1, Math.floor(width / 180));
  const estimatedLegendRows = Math.ceil(legendItemCount / estimatedItemsPerRow);
  return Math.max(LEGEND_HEIGHT, estimatedLegendRows * 24 + 16);
}

export interface XYChartProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[] | undefined | null;
  config: Partial<XYChartConfig>; // Allow partial config override
  width?: number; // Optional explicit width
  height?: number; // Optional explicit height
}

// Add this helper function at the top level
const formatDateTick = (date: Date, density: "low" | "medium" | "high") => {
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();

  switch (density) {
    case "low": // Few ticks, show full date
      return `${month} ${year}`;
    case "medium": // Medium density, abbreviate year
      return `${month} '${year.toString().slice(2)}`;
    case "high": // High density, very compact
      return month;
  }
};

const XYChart: React.FC<XYChartProps> = ({
  data: rawData,
  config: propsConfig,
  width: explicitWidth,
  height: explicitHeight,
}) => {
  // State to track current theme
  const [currentTheme, setCurrentTheme] = useState<"light" | "dark">(() => {
    const storedTheme = localStorage.getItem("theme") as "light" | "dark";
    return (
      storedTheme ||
      (window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light")
    );
  });

  // Effect to listen for theme changes
  useEffect(() => {
    // Function to update theme state
    const updateTheme = () => {
      const theme = localStorage.getItem("theme") as "light" | "dark";
      setCurrentTheme(
        theme ||
          (window.matchMedia &&
          window.matchMedia("(prefers-color-scheme: dark)").matches
            ? "dark"
            : "light")
      );
    };

    // Listen for storage events (when localStorage changes)
    window.addEventListener("storage", updateTheme);

    // Setup MutationObserver to detect theme changes via DOM attributes
    // This works when the theme is changed within the same window
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.attributeName === "data-theme" ||
          mutation.attributeName === "class"
        ) {
          updateTheme();
        }
      }
    });

    // Start observing document for theme changes
    observer.observe(document.documentElement, { attributes: true });

    return () => {
      window.removeEventListener("storage", updateTheme);
      observer.disconnect();
    };
  }, []);

  // 1. Get default settings
  const defaultSettings = useChartSettings();

  const layout = propsConfig.layout;
  const honorConfigTheme = layout?.honorConfigTheme === true;
  const resolvedTheme = honorConfigTheme
    ? (propsConfig.theme ?? currentTheme)
    : currentTheme;

  // 2. Merge default settings with component props config
  // propsConfig takes precedence
  const config: XYChartConfig = {
    ...defaultSettings,
    ...propsConfig,
    theme: resolvedTheme,
    layout,
    // Deep merge for nested objects like axes, grid, tooltip
    axes: {
      x: {
        position: "bottom", // Default required position
        ...defaultSettings.axes?.x,
        ...propsConfig.axes?.x,
      },
      y: {
        position: "left", // Default required position
        ...defaultSettings.axes?.y,
        ...propsConfig.axes?.y,
      },
    },
    grid: {
      showX: propsConfig.grid?.showX ?? defaultSettings.grid?.showX ?? false,
      showY: propsConfig.grid?.showY ?? defaultSettings.grid?.showY ?? true,
      ...defaultSettings.grid,
      ...propsConfig.grid,
    },
    tooltip: {
      enabled:
        propsConfig.tooltip?.enabled ??
        defaultSettings.tooltip?.enabled ??
        true,
      ...defaultSettings.tooltip,
      ...propsConfig.tooltip,
    },
    series: (propsConfig.series || defaultSettings.series || []).map(
      (series) => ({
        ...series,
        curve: propsConfig.smoothLines ? curveMonotoneX : curveLinear,
      })
    ),
    xKey: propsConfig.xKey || defaultSettings.xKey || "", // Ensure xKey is present
    yKeys: propsConfig.yKeys || defaultSettings.yKeys || [], // Ensure yKeys is present
  };

  // 3. Process data using the final config
  const { seriesData } = useChartData(rawData, config);

  // 4. Determine theme using custom themes
  const chartTheme = resolvedTheme === "dark" ? getDarkTheme() : getLightTheme();

  // Prepare a map for easy lookup of series config (e.g., label)
  // Moved before the early return to satisfy rules of hooks
  const seriesConfigMap = useMemo(() => {
    return (config.series || []).reduce(
      (acc, series, index) => {
        acc[series.key] = { ...series, originalIndex: index }; // Store original index for color
        return acc;
      },
      {} as Record<
        string,
        XYChartConfig["series"][0] & { originalIndex: number }
      >
    );
  }, [config.series]);

  // 5. Validate essential data (after all hooks)
  if (!seriesData.length || !config.xKey) {
    console.warn(
      "[XYChart] Early exit: Missing data or essential config keys."
    );
    return <div>Loading chart ...</div>;
  }

  const renderChart = ({
    width,
    height,
  }: {
    width: number;
    height: number;
  }) => {
    if (width <= 0 || height <= 0) return null;

    // Adjust chart dimensions based on legend position if legend is enabled
    const showLegend = config.showLegend ?? true;
    const legendPosition = (
      config.legendPosition === "right" ? "right" : "bottom"
    ) as "right" | "bottom";
    const legendMode = (config.legendMode || "inline") as "stacked" | "inline";
    const legendGap = config.legendGap ?? LEGEND_PADDING;
    const chartMargin = resolveChartMargin(config.padding);

    const legendItemsForEstimate = (config.series || []).map((series) => ({
      label: series.label || series.key,
    }));
    const bottomLegendHeightMode = config.layout?.bottomLegendHeight ?? "simple";
    const dynamicLegendHeight = computeBottomLegendHeight(
      bottomLegendHeightMode,
      legendItemsForEstimate,
      width,
      legendMode,
    );
    const bottomLegendOverflow = config.layout?.bottomLegendOverflow ?? "visible";
    const stackedLegendLabelLineClamp = config.layout?.stackedLegendLabelLineClamp;

    // Calculate dimensions based on legend position
    const chartWidth =
      showLegend && legendPosition === "right"
        ? width - LEGEND_WIDTH - legendGap
        : width;
    const chartHeight =
      showLegend && legendPosition === "bottom"
        ? height - dynamicLegendHeight - legendGap
        : height;

    // Calculate plot area dimensions
    const innerWidth = chartWidth - chartMargin.left - chartMargin.right;
    const innerHeight = chartHeight - chartMargin.top - chartMargin.bottom;

    // Ensure dimensions are positive
    if (innerWidth <= 0 || innerHeight <= 0) return null;

    // Calculate optimal number of x-axis ticks based on available width
    const calculateOptimalXTicks = () => {
      // If explicit numTicks is set in config, use that
      if (config.axes?.x?.numTicks !== undefined) {
        return {
          count: config.axes.x.numTicks,
          density: "medium" as const,
        };
      }

      // Get base tick count from width breakpoints
      let baseTicks;
      if (innerWidth <= WIDTH_BREAKPOINTS.SMALL) {
        baseTicks = MIN_X_TICKS;
      } else if (innerWidth <= WIDTH_BREAKPOINTS.MEDIUM) {
        baseTicks = MIN_X_TICKS + 1;
      } else if (innerWidth <= WIDTH_BREAKPOINTS.LARGE) {
        baseTicks = DEFAULT_X_TICKS;
      } else {
        baseTicks = MAX_X_TICKS;
      }

      // Calculate pixels per tick
      const pixelsPerTick = innerWidth / baseTicks;

      // Adjust tick count if spacing is too tight
      let finalTicks = baseTicks;
      if (pixelsPerTick < MIN_PIXELS_PER_TICK) {
        finalTicks = Math.max(
          MIN_X_TICKS,
          Math.floor(innerWidth / MIN_PIXELS_PER_TICK)
        );
      }

      // Determine density level based on available width per tick
      const density =
        pixelsPerTick >= TICK_DENSITY_THRESHOLDS.LOW
          ? ("low" as const)
          : pixelsPerTick >= TICK_DENSITY_THRESHOLDS.MEDIUM
            ? ("medium" as const)
            : ("high" as const);

      return {
        count: finalTicks,
        density,
      };
    };

    const { count: optimalXTicks, density } = calculateOptimalXTicks();

    // Smart tick formatter for dates
    const formatXTick = (value: any) => {
      if (!(value instanceof Date)) {
        return String(value);
      }

      // Get all dates in the dataset
      const allDates = seriesData[0]?.data.map((d) => d.x) || [];
      const isFirstOrLast = (date: Date) => {
        const timestamp = date.getTime();
        return (
          timestamp ===
            Math.min(
              ...allDates.map((d) => (d instanceof Date ? d.getTime() : 0))
            ) ||
          timestamp ===
            Math.max(
              ...allDates.map((d) => (d instanceof Date ? d.getTime() : 0))
            )
        );
      };

      // Always show year for first and last dates
      if (isFirstOrLast(value)) {
        return formatDateTick(value, "low");
      }

      return formatDateTick(value, density);
    };

    // Configure scales with actual inner dimensions
    const { xScale, yScale } = configureScales(
      config as any,
      seriesData,
      innerWidth,
      innerHeight
    );

    const patternId = "background-pattern-lines";
    const basePatternStrokeColor =
      currentTheme === "dark"
        ? getDarkTheme().gridStyles.stroke
        : getLightTheme().gridStyles.stroke;

    // Helper function to convert hex/named color to rgba with opacity
    const colorToRgba = (color: string, opacity: number): string => {
      // Basic hex handling (#RGB, #RRGGBB)
      if (color.startsWith("#")) {
        let hex = color.slice(1);
        if (hex.length === 3) {
          hex = hex
            .split("")
            .map((c) => c + c)
            .join("");
        }
        if (hex.length === 6) {
          const bigint = parseInt(hex, 16);
          const r = (bigint >> 16) & 255;
          const g = (bigint >> 8) & 255;
          const b = bigint & 255;
          return `rgba(${r}, ${g}, ${b}, ${opacity})`;
        }
      }
      // Add handling for other color formats (rgb, named colors) if needed
      // Fallback if conversion fails
      console.warn("[XYChart] Could not convert pattern color to RGBA:", color);
      return color; // Return original color as fallback
    };

    const patternStrokeColorWithOpacity = colorToRgba(
      basePatternStrokeColor || "#ccc",
      PATTERN_OPACITY
    );

    // Calculate Y-axis label offset dynamically based on tick label width
    const yAxisLabelOffset = (() => {
      if (!config.axes?.y) return 40;
      
      try {
        // Get tick values from the scale
        const tickValues = yScale.ticks ? yScale.ticks(config.axes.y.numTicks || 5) : [];
        
        // Format ticks using the axis formatter
        const formattedTicks = tickValues.map(v => 
          config.axes.y?.tickFormat ? config.axes.y.tickFormat(v) : String(v)
        );
        
        // Estimate max tick label width (rough estimate: ~7px per character for 11px font)
        const maxTickWidth = Math.max(
          ...formattedTicks.map(label => label.length * 7),
          30 // minimum width
        );
        
        // Add padding: tick width + gap + extra space for label
        return Math.max(maxTickWidth + 35, 55);
      } catch (error) {
        console.warn("[XYChart] Error calculating Y-axis label offset:", error);
        return 40; // fallback
      }
    })();

    // Get the appropriate text color based on theme
    const axisTitleColor =
      resolvedTheme === "dark"
        ? chartTheme.axisStyles?.x?.bottom?.axisLabel?.fill || "#f8fafc"
        : chartTheme.axisStyles?.x?.bottom?.axisLabel?.fill || "#334155";

    // Prepare legend items
    const legendItems: LegendItem[] = config.series.map((series) => ({
      label: series.label || series.key,
      value: 0,
      color: series.color,
      showValue: false,
      isDashed: series.isDashed,
    }));

    return (
      <div
        style={{
          width,
          height,
          display: "flex",
          flexDirection: legendPosition === "right" ? "row" : "column",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          gap: `${legendGap}px`,
        }}
      >
        <div
          style={{
            width: chartWidth,
            height: legendPosition === "right" ? height : chartHeight,
            position: "relative",
          }}
        >
          <VisxXYChart
            height={chartHeight}
            width={chartWidth}
            xScale={xScale}
            yScale={yScale}
            theme={chartTheme}
            margin={chartMargin}
          >
            {/* Define Pattern */}
            {config.backgroundPattern && (
              <PatternLines
                id={patternId}
                width={10}
                height={10}
                orientation={["diagonal"]}
                stroke={patternStrokeColorWithOpacity}
                strokeWidth={1}
              />
            )}

            {/* Background Rect */}
            {config.backgroundPattern && (
              <rect
                x={chartMargin.left}
                y={chartMargin.top}
                width={innerWidth}
                height={innerHeight}
                fill={`url(#${patternId})`}
              />
            )}

            {/* Render Grid */}
            {(config.grid?.showX || config.grid?.showY) && (
              <Grid
                stroke={config.grid?.stroke}
                numTicks={config.grid?.showY ? (config.axes?.y?.numTicks || 5) : optimalXTicks}
                lineStyle={{
                  stroke: config.grid?.stroke || chartTheme.gridStyles.stroke,
                }}
                rows={config.grid?.showY}
                columns={config.grid?.showX}
              />
            )}

            {/* Render Axes */}
            {config.axes?.x && config.showXAxis !== false && (
              <Axis
                orientation={config.axes.x.position || "bottom"}
                tickFormat={config.axes.x.tickFormat || formatXTick}
                numTicks={optimalXTicks}
                label={config.axes.x.title}
                labelOffset={config.padding?.bottom === undefined ? 60 : Math.min(56, Math.max(24, chartMargin.bottom - 2))}
                labelProps={{
                  fontSize: 12,
                  textAnchor: "middle",
                  transform: "rotate(0)",
                  dy: "-1em",
                  fill: "#94a3b8",
                  fontFamily: "monospace",
                  fontWeight: "normal",
                }}
                hideAxisLine={true}
                hideTicks={config.grid?.showX}
              />
            )}
            {config.axes?.y && config.showYAxis !== false && (
              <Axis
                orientation={config.axes.y.position || "left"}
                tickFormat={config.axes.y.tickFormat}
                numTicks={config.axes.y.numTicks}
                label={config.axes.y.title}
                labelOffset={yAxisLabelOffset}
                labelProps={{
                  fontSize: 12,
                  textAnchor: "middle",
                  transform: "rotate(-90)",
                  dy: "-3em",
                  fill: "#94a3b8",
                  fontFamily: "monospace",
                  fontWeight: "normal",
                }}
                hideAxisLine={true}
                hideTicks={config.grid?.showY}
              />
            )}

            {/* Render All Series */}
            {renderSeries(config, seriesData)}

            {/* Render Tooltip */}
            {config.tooltip?.enabled && (
              <Tooltip<TooltipData>
                snapTooltipToDatumX
                showVerticalCrosshair={config.tooltip?.showCrosshair ?? true}
                showSeriesGlyphs
                renderTooltip={({ tooltipData: td }: any) => {
                  if (!td) return null;
                  const xValue = td.nearestDatum?.datum.x;
                  if (xValue == null) return null;

                  const formatDate = (d: any) =>
                    d instanceof Date ? d.toLocaleDateString() : String(d);
                  const formatValue = (v: any) => {
                    if (typeof v === "number") {
                      // Check if we're dealing with percentage data
                      const isPercentageChart =
                        config.axes?.y?.title?.includes("%") ||
                        config.axes?.y?.title
                          ?.toLowerCase()
                          .includes("allocation");
                      if (isPercentageChart) {
                        return `${v.toFixed(1)}%`;
                      }
                      return v.toFixed(2);
                    }
                    return String(v ?? "N/A");
                  };

                  // Check if this is a resource-based chart (exposure chart)
                  const isResourceChart =
                    !(xValue instanceof Date) &&
                    (config.axes?.x?.title
                      ?.toLowerCase()
                      .includes("resource") ||
                      config.axes?.y?.title
                        ?.toLowerCase()
                        .includes("allocation"));

                  return (
                    <div className="p-2">
                      {/* Header: Show resource name prominently for resource charts, date for time-based charts */}
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 border-b border-gray-200 dark:border-gray-600 pb-1">
                        {isResourceChart ? (
                          // For resource charts, show the full resource name prominently
                          <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              Resource:
                            </span>
                            <div className="font-semibold">
                              {String(xValue)}
                            </div>
                          </div>
                        ) : xValue instanceof Date ? (
                          // For date-based charts, show formatted date
                          formatDate(xValue)
                        ) : (
                          // Fallback for other types
                          String(xValue)
                        )}
                      </div>

                      {/* Data values */}
                      <div className="space-y-1">
                        {config.series.map((series) => {
                          const entry = td.datumByKey?.[series.key];
                          const yVal = entry?.datum.y;

                          const swatchStyle: React.CSSProperties = {
                            width: "0.75rem", // Increased from 0.5rem (w-3)
                            height: "0.75rem", // Increased from 0.5rem (h-3)
                            borderRadius: "50%",
                            backgroundColor: series.isDashed
                              ? "transparent"
                              : series.color,
                            border: series.isDashed
                              ? `2px dashed ${series.color}`
                              : "none",
                            boxSizing: "border-box",
                          };

                          return (
                            <div
                              key={series.key}
                              className="flex items-center gap-2"
                            >
                              <div style={swatchStyle} />
                              <span className="text-sm text-gray-900 dark:text-gray-100">
                                {series.label || series.key}:
                              </span>
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {formatValue(yVal)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }}
              />
            )}
          </VisxXYChart>
        </div>

        {/* Render Legend */}
        {showLegend && (
          <div
            style={{
              width: legendPosition === "right" ? LEGEND_WIDTH : chartWidth,
              height: legendPosition === "right" ? chartHeight : "auto",
              minHeight:
                legendPosition === "bottom" ? LEGEND_HEIGHT : undefined,
              ...(legendPosition === "bottom" &&
              bottomLegendHeightMode === "wrapEstimate"
                ? { height: dynamicLegendHeight, minHeight: undefined }
                : {}),
              overflow:
                legendPosition === "right"
                  ? "auto"
                  : legendPosition === "bottom" && bottomLegendOverflow === "hidden"
                    ? "hidden"
                    : "visible",
              display: "flex",
              alignItems: legendPosition === "right" ? "center" : "flex-start",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <ChartLegend
              items={legendItems}
              position={legendPosition}
              mode={legendMode}
              className="chart-legend"
              showValues={false}
              isDarkMode={honorConfigTheme ? resolvedTheme === "dark" : undefined}
              labelLineClamp={
                legendPosition === "bottom" &&
                legendMode === "stacked" &&
                stackedLegendLabelLineClamp !== undefined
                  ? stackedLegendLabelLineClamp
                  : undefined
              }
            />
          </div>
        )}
      </div>
    );
  };

  // Use ParentSize for responsive rendering unless explicit dimensions are provided
  if (explicitWidth !== undefined && explicitHeight !== undefined) {
    return renderChart({ width: explicitWidth, height: explicitHeight });
  }

  return (
    <ParentSize>
      {({ width, height }) => {
        return renderChart({ width, height });
      }}
    </ParentSize>
  );
};

export default XYChart;
